import './index.scss';
import React, {lazy} from 'react';
import * as serviceWorker from './serviceWorker';
import {createRoot} from "react-dom/client";
import {WpClient} from "./utils/wp-client";
import {cfg} from './config';
import {WpContextData} from "./components/context/WordPressProvider.tsx";
import MiscScripts from "./utils/misc-scripts.ts";
import {GoogleReCaptchaProvider} from "react-google-recaptcha-v3";

// note: always lazy-import AppContainer (it saves 300kb+ from build files)
const AppContainer = lazy(() => import("./AppContainer.tsx"));

/**
 * DOM element(s) on which this React app/module will be loaded
 */

window.wpApiSettings = window.wpApiSettings || {};
const {react} = cfg;

/**
 * Returns the existing React root for `el`, or creates and attaches one.
 * Workaround: Vite HMR re-executes this module on every hot update, which would
 * call createRoot() twice on the same element and trigger a React warning while
 * duplicating the rendered tree. A module-level Map would be reset on each
 * re-execution for the same reason, so the root is stored directly on the DOM
 * node — it survives module re-evaluation because the DOM itself is not reloaded.
 */
function getOrCreateRoot(el: HTMLElement): ReturnType<typeof createRoot> {
    const key = '__reactRoot';
    if (!(el as any)[key]) {
        (el as any)[key] = createRoot(el);
    }
    return (el as any)[key];
}

// extract generic relevant context data to pass to each root module
const initialWpContext = (): WpContextData<ZWindowData> => {
    const {rest_url, rest_nonce, upload_url} = window.zdata;
    return {
        api: new WpClient({
            baseURL: `${rest_url}${cfg.wp.custom}`,
            // only non-null when WP authenticated user (like admin); else defaults to basic API auth
            headers: {'X-WP-Nonce': rest_nonce || null}
        }),
        upload: upload_url!,
        is_fr: window.zdata.lang == "fr",
        others: window.zdata,
    }
}

// attach React modules to DOM nodes
reactLoadPublicModules();
reactLoadPrivateModules();
reactLoadAdminModules();
reactLoadTestModules();

// load miscellaneous non-React scripts on document.ready, etc.
const miscScripts = new MiscScripts();
miscScripts.homeAutoSwitchVideoAndCarouselModules();
miscScripts.fixWpmlAmpersand();
miscScripts.fixGfPopupForms();
// miscScripts.autoScrollElementorPagination();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below, though this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

/**
 * Public modules are available to all visitors of the site, regardless of whether they're logged in or nots
 */
function reactLoadPublicModules() {
    // (public) single property listing - Elementor React module
    const publicListingSingle = document.getElementById(react.ids.public_listing_single);
    (($this: HTMLElement | null) => {
        if (!$this) return;

        // url looks like /print-listing/(en|fr)/(mls-id)
        const isPrint = /print-listing\/(en|fr)\/([0-9]*)/i.test(window.location.href);
        const template = isPrint
            ? "Public.ListingSingle.Print"
            : "Public.ListingSingle";
        const Component = lazy(() => import((`./views/${template}.tsx`)));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        );
    })(publicListingSingle);

    // (public) all listings as either GRID | MAP - Elementor React module
    const publicListings = document.getElementById(react.ids.public_listings);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Public.Listings.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        );
    })(publicListings);

    // (public/dev) temporary title audit list for checking computed listing titles at a glance
    const publicDevTitleReview = document.getElementById(react.ids.public_dev_title_review);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Public.DevTitleReview.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        );
    })(publicDevTitleReview);

    // (public) standalone search module - Elementor React module
    const publicStandaloneSearch = document.getElementById(react.ids.public_standalone_search);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Public.StandaloneSearch.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        );
    })(publicStandaloneSearch);

    // (public) McGill project shuffler - Elementor React module (no UI, only JS mechanism)
    const publicProjectShuffler = document.getElementById(react.ids.public_project_shuffler);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Public.ProjectShuffler.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        );
    })(publicProjectShuffler);
}

/**
 * Private modules are restricted via simple password mechanism
 */
function reactLoadPrivateModules() {

    // (private) broker portal which then leads to seller report for individual listings
    const brokerPortal = document.getElementById(react.ids.private_broker_portal);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Private.BrokerPortal.tsx"));
        const AuthProvider = lazy(() => import("./components/context/BrokerPortal.AuthProvider.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <GoogleReCaptchaProvider reCaptchaKey={"6LeFPForAAAAADXbCEziUxm4HvmJb7HPIaXQDw3c"} useEnterprise>
                    <AuthProvider>
                        <Component/>
                    </AuthProvider>
                </GoogleReCaptchaProvider>
            </AppContainer>
        );
    })(brokerPortal);

    // (private) seller report for a single listing
    const sellerReport = document.getElementById(react.ids.private_seller_report);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Private.SellerReport.tsx"));
        const AuthProvider = lazy(() => import("./components/context/SellerReport.AuthProvider.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <AuthProvider>
                    <Component/>
                </AuthProvider>
            </AppContainer>
        );
    })(sellerReport);
}

/**
 * Admin modules are available only for WordPress admins logged in to the dashboard
 */
function reactLoadAdminModules() {
    // (admin) centris custom labels to define columns in fields of dynamic tables (meta_fields mongo collection)
    const adminCentrisLabels = document.getElementById(react.ids.admin_centris_labels);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Admin.Centris.Labels.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        )
    })(adminCentrisLabels);

    // (admin) centris dictionary of static tables and their values
    const adminCentrisDictionary = document.getElementById(react.ids.admin_centris_dictionary);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Admin.Centris.Dictionary.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        )
    })(adminCentrisDictionary);

    // (admin) centris requests / filters to determine which properties & brokers to display in this site
    const adminCentrisRequests = document.getElementById(react.ids.admin_centris_requests);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Admin.Centris.Requests.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        )
    })(adminCentrisRequests);

    // (admin) centris brokers
    const adminCentrisBrokers = document.getElementById(react.ids.admin_centris_brokers);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Admin.Centris.Brokers.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        )
    })(adminCentrisBrokers);

    // (admin) centris broker listings redirections from Centris
    const adminCentrisRedirects = document.getElementById(react.ids.admin_centris_redirects);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Admin.Centris.Redirects.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        )
    })(adminCentrisRedirects);

    // (admin) customizable React frontend texts
    const adminCentrisReactexts = document.getElementById(react.ids.admin_centris_reactexts);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Admin.Centris.Reactexts.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        )
    })(adminCentrisReactexts);

    // (admin) new INSCRIPTIONS manual creation form
    const adminCentrisNewInscription = document.getElementById(react.ids.admin_centris_exclusive_property);
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Admin.Centris.ExclusiveProperty.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        )
    })(adminCentrisNewInscription);
}

function reactLoadTestModules() {
    // todo test Elastic's Search UI for autocomplete and such like
    const searchUi = document.getElementById("test-search-ui");
    (($this: HTMLElement | null) => {
        if (!$this) return;
        const Component = lazy(() => import("./views/Public.Test.SearchUI.tsx"));
        getOrCreateRoot($this).render(
            <AppContainer wp={{...initialWpContext(), others: window.zdata}}>
                <Component/>
            </AppContainer>
        );
    })(searchUi);
}
